import { useState, useRef } from 'react';
import type { MouseEvent } from 'react';
import type { ElementType, HTMLProps, Ref } from 'react';
import { forwardRef } from 'react';
import { colors, fontSizes } from '@anyroad/theme';
import type { IconProps } from '@anyroad/icon';
import { Icon } from '@anyroad/icon';
import styled from '@emotion/styled';
import { mergeRefs } from '@anyroad/util';

export type BasicInputProps = HTMLProps<HTMLInputElement> & {
  as?: ElementType<any>;
  dataTestId?: string;
  hint?: string;
  icon?: IconProps['name'];
  iconRight?: boolean;
  label?: string;
  labelComponent?: any;
  onChangeProp?: any;
  ref?: Ref<HTMLInputElement>;
  showClearIcon?: boolean;
  type: 'email' | 'number' | 'password' | 'tel' | 'text' | 'url';
};

const StyledInput = styled.input({
  width: '100%',
  padding: '14px 16px',
  height: '44px',
  boxSizing: 'border-box',
  border: `1px solid ${colors.borderAction}`,
  borderRadius: '0px',
  background: 'transparent',
  fontSize: fontSizes.large,
  ['::placeholder' as any]: {
    color: colors.text.placeholder,
    fontSize: fontSizes.large,
  },
});

export const StyledLabel = styled.label({
  display: 'block',
  margin: '0px 0px 10px',
  textAlign: 'left',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: fontSizes.large,
  color: colors.text.primary,
  lineHeight: '20px',
  fontWeight: '600',
  width: 'initial',
});

const StyledIcon = styled(Icon)({
  position: 'absolute',
  left: '0',
  margin: '16px 8px',
});

const ClearIcon = styled(Icon)({
  position: 'absolute',
  right: '0',
  margin: '15px 10px',
});

const Container = styled.div({
  position: 'relative',
});

const StyledHint = styled.span({
  display: 'block',
  textAlign: 'right',
  fontSize: fontSizes.small,
  color: colors.text.placeholder,
  marginTop: '4px',
});

export const BasicInput = forwardRef<HTMLInputElement, BasicInputProps>(
  (
    {
      dataTestId,
      label,
      icon,
      iconRight,
      style,
      disabled,
      labelComponent,
      onChangeProp,
      showClearIcon,
      width,
      hint,
      ...rest
    }: BasicInputProps,
    forwardedRef
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const mergedRef = mergeRefs([forwardedRef, inputRef]);

    const [inputValue, setInputValue] = useState<string>(
      (rest.value as string) || ''
    );

    const handleClearClick = (e: MouseEvent) => {
      setInputValue('');
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.value = '';
        onChangeProp?.(e);
      }
    };

    return (
      <div style={{ width }}>
        {label ? <StyledLabel htmlFor={label}>{label}</StyledLabel> : null}
        {labelComponent ? labelComponent : null}
        <Container>
          <StyledInput
            data-testid={dataTestId}
            disabled={disabled || false}
            id={label}
            onChange={(e) => {
              setInputValue(e.target.value);
              onChangeProp?.(e);
            }}
            ref={mergedRef}
            style={{
              cursor: disabled ? 'not-allowed' : 'text',
              opacity: disabled ? '.5' : '1',
              paddingLeft: icon && !iconRight ? '30px' : '16px',
              paddingRight:
                (icon && iconRight) || showClearIcon ? '30px' : '16px',
              ...style,
            }}
            {...rest}
          />
          {icon && !(inputValue && showClearIcon) ? (
            <StyledIcon
              height={14}
              name={icon}
              style={{
                left: iconRight ? 'auto' : '0',
                right: iconRight ? '0' : 'auto',
              }}
              width={14}
            />
          ) : null}
          {inputValue && showClearIcon ? (
            <ClearIcon
              height={14}
              name="CancelMsg"
              onClick={handleClearClick}
              width={14}
            />
          ) : (
            <></>
          )}
        </Container>
        {Boolean(hint) && <StyledHint>{hint}</StyledHint>}
      </div>
    );
  }
);

BasicInput.displayName = 'BasicInput';
